import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, message } from 'antd';
import { useTranslation } from '../../../i18n';

type BindEmailModalProps = {
  open: boolean;
  currentAddress?: string;
  onClose: () => void;
  onBind: (address: string) => Promise<void>;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function BindEmailModal({ open, currentAddress, onClose, onBind }: BindEmailModalProps) {
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAddress(currentAddress ?? '');
    }
  }, [open, currentAddress]);

  const submit = async () => {
    const value = address.trim();
    if (!EMAIL_PATTERN.test(value)) {
      message.warning(t('email.invalid_address'));
      return;
    }
    setSaving(true);
    try {
      await onBind(value);
      message.success(t('email.bound', { address: value }));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t('email.bind_mine')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={460}
      closeIcon={
        <span className="cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700">{t('common.close')}</span>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" className="wb-btn-primary rounded-lg" loading={saving} onClick={submit}>
            {t('email.bind')}
          </Button>
        </div>
      }
    >
      <div className="space-y-2 pt-1">
        <Input
          placeholder="name@company.com"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onPressEnter={submit}
        />
        <p className="mb-0 text-xs leading-relaxed text-slate-400">
          {t('email.bind_hint')}
        </p>
      </div>
    </Modal>
  );
}
